import {
	Component,
	ViewChild,
	OnInit,
	OnDestroy
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { Marker } from 'ngx/go-modules/src/interfaces/markers/marker';
import { CommentFilterService } from 'ngx/go-modules/src/services/comment-filter/comment-filter.service';
import { CommentListService } from 'ngx/go-modules/src/services/comment-list/comment-list.service';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'marker-filter-dropdown',
	template: require('./marker-filter-dropdown.component.html'),
	styles: [require('./marker-filter-dropdown.component.scss')]
})
export class MarkerFilterDropdownComponent implements OnInit, OnDestroy {
	public uniqueMarkers$ = new BehaviorSubject<Marker[]>([]);
	public form = new FormGroup({
		selectedMarkers: new FormControl([0])
	});
	public aiMarkers: Marker[] = [];
	public activityMarkers: Marker[] = [];
	@ViewChild('allSelected') private allSelected: MatOption;
	private destroy$ = new Subject<void>();

	constructor (
		public commentFilterService: CommentFilterService,
		public commentListService: CommentListService
	) {}

	public ngOnInit () {
		this.commentListService.comments$
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				const comments = this.commentListService.currentComments;
				const previousMarkers = [...this.uniqueMarkers$.value];
				const uniqueMarkers = this.commentFilterService.getUniqueMarkers(comments);
				this.uniqueMarkers$.next(uniqueMarkers);

				if (previousMarkers.length === 0 && uniqueMarkers.length > 0) {
					// select all when unique markers are first populated
					this.form.controls.selectedMarkers.patchValue([
						...uniqueMarkers.map((marker) => marker.tag_id), 0
					]);
					this.commentFilterService.selectedMarkerIds = [];
				} else if (uniqueMarkers.length > previousMarkers.length) {
					const newMarkers = uniqueMarkers.filter((marker) => {
						return !previousMarkers.some((prevMarker) => {
							return prevMarker.tag_id === marker.tag_id;
						});
					});
					this.form.controls.selectedMarkers.patchValue([
						...this.form.controls.selectedMarkers.value,
						...newMarkers.map((marker) => marker.tag_id)
					]);
					if (this.allSelected && !this.allSelected.selected) {
						this.commentFilterService.selectedMarkerIds = this.form.controls.selectedMarkers.value;
					}
				} else if (uniqueMarkers.length < previousMarkers.length) {
					const removedMarkers = previousMarkers.filter((marker) => {
						return !uniqueMarkers.some((currentMarker) => {
							return currentMarker.tag_id === marker.tag_id;
						});
					});
					this.form.controls.selectedMarkers.patchValue(
						this.form.controls.selectedMarkers.value.filter((id) => {
							return !removedMarkers.some((marker) => marker.tag_id === id);
						})
					);
					if (this.allSelected && this.allSelected.selected) {
						this.commentFilterService.selectedMarkerIds = [];
					} else if (this.form.controls.selectedMarkers.value.length === 0) {
						this.commentFilterService.selectedMarkerIds = [-1];
					} else {
						this.commentFilterService.selectedMarkerIds = this.form.controls.selectedMarkers.value;
					}
				}

				this.aiMarkers = uniqueMarkers.filter((marker) => marker.was_ai_generated);
				this.activityMarkers = uniqueMarkers.filter((marker) => !marker.was_ai_generated);

				if (previousMarkers.length !== uniqueMarkers.length) {
					this.commentListService.filterChanged();
				}
			});
	}

	public ngOnDestroy () {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public toggleOne () {
		if (this.allSelected.selected) {
			this.allSelected.deselect();
			this.commentFilterService.selectedMarkerIds = this.form.controls.selectedMarkers.value;
			// if only one marker exists and it is deselected
			if (this.form.controls.selectedMarkers.value.length === 0) {
				this.commentFilterService.selectedMarkerIds = [-1];
			}
		} else {
			if (this.form.controls.selectedMarkers.value.length === this.uniqueMarkers$.value.length) {
				this.allSelected.select();
				this.form.controls.selectedMarkers
					.patchValue([...this.uniqueMarkers$.value.map((marker) => marker.tag_id), 0]);
				this.commentFilterService.selectedMarkerIds = [];
			} else if (this.form.controls.selectedMarkers.value.length === 0) {
				this.form.controls.selectedMarkers.patchValue([]);
				this.commentFilterService.selectedMarkerIds = [-1];
			} else {
				this.commentFilterService.selectedMarkerIds = this.form.controls.selectedMarkers.value;
			}
		}
		this.commentListService.filterChanged();
	}

	public toggleAll () {
		if (this.allSelected.selected) {
			this.form.controls.selectedMarkers
				.patchValue([...this.uniqueMarkers$.value.map((marker) => marker.tag_id), 0]);
			this.commentFilterService.selectedMarkerIds = [];
		} else {
			this.form.controls.selectedMarkers.patchValue([]);
			this.commentFilterService.selectedMarkerIds = [-1];
		}
		this.commentListService.filterChanged();
	}
}
